// src/experiments/flags.js
//
// Feature flags. A flag's value is, in order of precedence: a QA override
// (`?flag=key:value`, remembered for the tab), the remote document's `flags`
// section, then the code default in flags.config.js. Remote values apply on
// the next route change, like experiments, and the last valid document is
// applied from cache before first paint.
//
// Two kinds of subscriber: React components through useFlag(), which are
// only notified outside a render, and plain listeners through
// subscribeFlags(), which run immediately on every change. The latter is how
// a module-level constant such as ENABLE_OTP_SMS follows its flag.

import { useSyncExternalStore } from 'react';
import { FLAGS } from './flags.config';

const PARAM = 'flag';
const SESSION_KEY = 'hippo_flag_override';
const TOKEN = /^[a-z0-9_-]+$/i;

let remote = {};
let remoteSource = 'code'; // code | cached | remote
let generation = 0;
const reactListeners = new Set();
const plainListeners = new Set();

function coerce(raw, defaultValue) {
  if (typeof defaultValue === 'boolean') {
    if (['1', 'true', 'on', 'yes'].includes(raw)) return true;
    if (['0', 'false', 'off', 'no'].includes(raw)) return false;
    return undefined;
  }
  if (typeof defaultValue === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (typeof defaultValue === 'string') return raw;
  return undefined;
}

// Validates the remote `flags` section. Values must match the type of the
// code default; unknown keys are ignored, like unknown experiment keys.
export function validateFlags(doc, defaults = FLAGS) {
  if (doc === null || doc === undefined) {
    return { ok: true, entries: {}, ignored: [] };
  }
  if (typeof doc !== 'object' || Array.isArray(doc)) {
    return { ok: false, errors: ['flags must be an object'] };
  }
  const errors = [];
  const ignored = [];
  const entries = {};
  for (const [key, value] of Object.entries(doc)) {
    if (!(key in defaults)) {
      ignored.push(key);
      continue;
    }
    const expected = typeof defaults[key];
    if (typeof value !== expected) {
      errors.push(`"${key}": must be a ${expected}`);
    } else if (expected === 'number' && !Number.isFinite(value)) {
      errors.push(`"${key}": must be a finite number`);
    } else {
      entries[key] = value;
    }
  }
  return errors.length ? { ok: false, errors } : { ok: true, entries, ignored };
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSession(value) {
  try {
    if (value && Object.keys(value).length) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // storage blocked
  }
}

// `?flag=otp_sms:0` (comma-separated for several) forces values for this
// tab; `?flag=clear` forgets them. Booleans accept 1/0, true/false, on/off.
export function captureFlagOverridesFromSearch(search, defaults = FLAGS) {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return readSession();
  }
  if (!params.has(PARAM)) return readSession();

  const raw = params.get(PARAM);
  if (raw === '' || raw === 'clear') {
    writeSession(null);
    return {};
  }
  const merged = { ...readSession() };
  for (const entry of raw.split(',')) {
    const [key, value] = entry.split(':').map((s) => s.trim());
    if (!key || value === undefined || !TOKEN.test(key)) continue;
    if (!(key in defaults)) continue;
    const coerced = coerce(value, defaults[key]);
    if (coerced !== undefined) merged[key] = coerced;
  }
  writeSession(merged);
  return merged;
}

export function getFlagOverrides() {
  return typeof window !== 'undefined'
    ? captureFlagOverridesFromSearch(window.location.search)
    : {};
}

function notify({ react }) {
  generation += 1;
  plainListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // a listener must never break the layer
    }
  });
  if (react) reactListeners.forEach((listener) => listener());
}

// Installs the remote `flags` section. `notify: false` is for a document
// applied during a route change, where components about to render read the
// new value anyway and nothing on screen should flip mid-render.
export function setRemoteFlags(entries, source, { notify: react = true } = {}) {
  remote = { ...(entries || {}) };
  remoteSource = source || 'code';
  notify({ react });
}

export function getFlag(key, defaults = FLAGS) {
  try {
    if (!(key in defaults)) return undefined;
    const overrides = getFlagOverrides();
    if (Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key];
    }
    if (Object.prototype.hasOwnProperty.call(remote, key)) return remote[key];
    return defaults[key];
  } catch {
    return defaults[key];
  }
}

export function getFlagSource(key) {
  try {
    if (Object.prototype.hasOwnProperty.call(getFlagOverrides(), key)) {
      return 'override';
    }
    if (Object.prototype.hasOwnProperty.call(remote, key)) return remoteSource;
  } catch {
    // fall through
  }
  return 'code';
}

// For the debug overlay: every flag with its value and where it came from.
export function getFlagsState(defaults = FLAGS) {
  return Object.keys(defaults).map((key) => ({
    key,
    value: getFlag(key, defaults),
    defaultValue: defaults[key],
    source: getFlagSource(key),
  }));
}

export function subscribeFlags(listener) {
  plainListeners.add(listener);
  return () => plainListeners.delete(listener);
}

function subscribeReact(listener) {
  reactListeners.add(listener);
  return () => reactListeners.delete(listener);
}

function getGeneration() {
  return generation;
}

export function useFlag(key) {
  useSyncExternalStore(subscribeReact, getGeneration, getGeneration);
  return getFlag(key);
}

export function _resetFlagsForTests() {
  remote = {};
  remoteSource = 'code';
  notify({ react: true });
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export const FLAG_SESSION_KEY = SESSION_KEY;
